// Backlog Management API Service

import apiClient from '@/api/client';
import type {
  Backlog,
  BacklogItem,
  CreateBacklogItemRequest,
  UpdateBacklogItemRequest,
  DevPriorityTicket,
  PriorityUpdateModel,
  SetPerceivedPriorityModel,
} from './types';

const BACKLOG_API_BASE = '/api/backlog';
const DEV_PRIORITY_API_BASE = '/api/devPriority';

/**
 * Get all available backlogs
 */
export const getBacklogs = async (): Promise<Backlog[]> => {
  // TODO: Implement when backend API is ready
  // For now, return sample data
  return [
    { id: 1, title: 'Service Backlog' },
    { id: 2, title: 'Development Backlog' },
  ];
};

/**
 * Get a specific backlog with its items
 */
export const getBacklog = async (id: number): Promise<Backlog> => {
  // TODO: Implement when backend API is ready
  // For now, return sample data
  return {
    id,
    title: 'Example Backlog',
    items: [
      {
        id: '1',
        order: 3,
        title: 'Example Item',
        description: 'This is an example item',
        notes: [],
        status: 'In Progress',
        requestedBy: 'User',
        lastModified: new Date(),
        lastModifiedBy: 'User',
        createdOn: new Date(),
        createdBy: 'User',
        position: 3,
      },
      {
        id: '2',
        order: 1,
        title: 'Another Example Item',
        description: 'Another description',
        notes: [],
        status: 'Not Started',
        requestedBy: 'User',
        lastModified: new Date(),
        lastModifiedBy: 'User',
        createdOn: new Date(),
        createdBy: 'User',
        position: 1,
      },
      {
        id: '3',
        order: 2,
        title: 'Yet Another Example Item',
        description: 'Yet another description',
        notes: [],
        status: 'Done',
        requestedBy: 'User',
        lastModified: new Date(),
        lastModifiedBy: 'User',
        createdOn: new Date(),
        createdBy: 'User',
        position: 2,
      },
    ],
  };
};

/**
 * Create a new backlog item
 */
export const createBacklogItem = async (
  backlogId: number,
  item: CreateBacklogItemRequest
): Promise<BacklogItem> => {
  const response = await apiClient.post<BacklogItem>(`${BACKLOG_API_BASE}/${backlogId}/items`, item);
  return response.data;
};

/**
 * Update a backlog item
 */
export const updateBacklogItem = async (
  backlogId: number,
  item: UpdateBacklogItemRequest
): Promise<BacklogItem> => {
  const response = await apiClient.put<BacklogItem>(
    `${BACKLOG_API_BASE}/${backlogId}/items/${item.id}`,
    item
  );
  return response.data;
};

/**
 * Delete a backlog item
 */
export const deleteBacklogItem = async (backlogId: number, itemId: string): Promise<void> => {
  await apiClient.delete(`${BACKLOG_API_BASE}/${backlogId}/items/${itemId}`);
};

/**
 * Reorder backlog items
 */
export const reorderBacklogItems = async (
  backlogId: number,
  items: { id: string; position: number }[]
): Promise<void> => {
  await apiClient.post(`${BACKLOG_API_BASE}/${backlogId}/reorder`, { items });
};

/**
 * Get dev priority tickets
 */
export const getDevPriorityTickets = async (): Promise<DevPriorityTicket[]> => {
  const response = await apiClient.get<DevPriorityTicket[]>(`${DEV_PRIORITY_API_BASE}/priorities`);
  return response.data;
};

/**
 * Update ticket perceived priority
 */
export const updateTicketPriority = async (
  ticketId: number,
  priority: number
): Promise<void> => {
  const model: SetPerceivedPriorityModel = { ticketId, priority };
  await apiClient.post(`${DEV_PRIORITY_API_BASE}/setPerceivedPriority`, model);
};

/**
 * Reorder ticket by moving it to a new index
 */
export const reorderTicket = async (
  ticketId: number,
  newIndex: number
): Promise<DevPriorityTicket[]> => {
  const model: PriorityUpdateModel = { ticketId, newIndex };
  const response = await apiClient.post<DevPriorityTicket[]>(`${DEV_PRIORITY_API_BASE}/updatePriority`, model);
  return response.data;
};

/**
 * Get user type (Developer or User)
 */
export const getUserType = async (): Promise<string> => {
  const response = await apiClient.get(`${DEV_PRIORITY_API_BASE}/userType`, {
    responseType: 'text',
  });
  // The response is a plain text string, not JSON
  return response.data as string;
};
